import { Icon, cn } from "@sagot/ui";
import { useState } from "react";
import { useCaptcha } from "../actions/captcha";
import type { ActionCardHostProps } from "../actions/card-props";
import type { WorkflowChoice, WorkflowStepPayload } from "../workflow/contract";
import { useCountdownTo } from "../workflow/hooks";
import type { ResolvedWorkflowLabels } from "../workflow/labels";
import { isWorkflowCodeSend, type WorkflowAnswerExtras } from "../workflow/step";
import { primaryActionClass, useChatTheme } from "./chat-theme";

export interface WorkflowVerifyCardProps {
  card: WorkflowStepPayload;
  labels: ResolvedWorkflowLabels;
  /** Another turn is in flight: nothing can be answered right now. */
  disabled: boolean;
  /** Id of the element holding the step text (the bot bubble): the panel is described by it. */
  describedBy: string | undefined;
  onAnswer: (card: WorkflowStepPayload, choice: WorkflowChoice, extras?: WorkflowAnswerExtras) => void;
  /** The host's single-use bot check, shown under "Send code" / "Resend code" (web app on the platform origin). */
  renderCaptcha?: ActionCardHostProps["renderCaptcha"];
}

/** A local clock time ("9:10 AM") for the moment the code stops working; empty when the date is not one. */
function clockTime(iso: string | undefined): string {
  const at = iso ? new Date(iso) : null;
  if (!at || Number.isNaN(at.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(at);
}

interface NoteProps {
  icon: "mail" | "clock" | "shield-check";
  children: string;
}

function Note({ icon, children }: NoteProps) {
  return (
    <p className="flex items-start gap-2 text-sm text-fg-muted">
      <Icon name={icon} className="mt-0.5 size-4 shrink-0 text-fg-subtle" />
      <span className="min-w-0 break-words">{children}</span>
    </p>
  );
}

/**
 * The open card of a `verify_email` step. Stage "send": the masked address and a "Send code" button. Stage "enter": where
 * the code went, when it stops working, the tries left, and "Resend code" (once, a minute after the first code). The code
 * itself is typed in the composer, which the host switches to its reply mode while this card is open.
 *
 * Both buttons make the server e-mail a code, so both carry the host's bot check: the card takes the token from the
 * host's widget and hands it to `onAnswer` (the host forwards it as `turnstile_token`).
 */
export function WorkflowVerifyCard({ card, labels, disabled, describedBy, onAnswer, renderCaptcha }: WorkflowVerifyCardProps) {
  const { themed } = useChatTheme();
  const otp = card.otp;
  const sending = otp?.stage === "send";
  const button = card.buttons[0];
  const secondsLeft = useCountdownTo(sending ? undefined : otp?.resend_at);
  const [counted] = useState(secondsLeft > 0);
  const [attempts, setAttempts] = useState(0);
  const [waitingForCheck, setWaitingForCheck] = useState(false);
  const captcha = useCaptcha(button ? renderCaptcha : undefined, attempts);
  if (!otp) return null;

  const press = (): void => {
    if (!button) return;
    const choice: WorkflowChoice = { button_id: button.id };
    if (!renderCaptcha || !isWorkflowCodeSend(choice)) {
      onAnswer(card, choice);
      return;
    }
    const token = captcha.take();
    setWaitingForCheck(token === null);
    if (token === null) return;
    setAttempts((count) => count + 1);
    onAnswer(card, choice, { captchaToken: token });
  };

  const email = otp.email_masked;
  const expires = clockTime(otp.expires_at);
  const serverInvalid = card.error === "invalid";
  const problem = waitingForCheck ? labels.securityCheckWait : serverInvalid ? labels.invalidFromServer : "";
  const resendClosed = !sending && secondsLeft > 0;
  const ready = !sending && counted && secondsLeft === 0 && button !== undefined;
  return (
    <div role="group" aria-label={labels.verifyGroup} aria-describedby={describedBy} className="mt-2 space-y-2 rounded-lg border border-line bg-surface p-3">
      <Note icon="mail">{sending ? labels.codeWillGoTo(email) : labels.codeSentTo(email)}</Note>
      {!sending && expires ? <Note icon="clock">{labels.codeExpiresAt(expires)}</Note> : null}
      {!sending && otp.attempts_left !== undefined ? <Note icon="shield-check">{labels.triesLeft(otp.attempts_left)}</Note> : null}
      <p aria-live="polite" className={problem ? "flex items-center gap-1.5 text-xs font-semibold text-danger" : "sr-only"}>
        {problem ? <Icon name="alert-circle" className="size-3.5 shrink-0" /> : null}
        {problem || (ready ? labels.resendReady : "")}
      </p>
      {button && captcha.node ? (
        <div role="group" aria-label={labels.securityCheck} className="flex justify-center">
          {captcha.node}
        </div>
      ) : null}
      {button ? (
        <button type="button" disabled={disabled || resendClosed} onClick={press} className={cn(sending ? primaryActionClass(themed) : "btn btn-secondary", "min-h-11 w-full")}>
          {button.label || (sending ? labels.sendCode : labels.resendCode)}
        </button>
      ) : null}
      {resendClosed && button ? <p className="text-xs text-fg-muted">{labels.resendIn(secondsLeft)}</p> : null}
    </div>
  );
}
